export function createAgentObservation(code, severity, message, options = {}) {
    return {
        code,
        severity,
        path: options.path || "",
        message,
        questionId: options.questionId || "",
        suggestedAction: options.suggestedAction || ""
    };
}

export function buildAgentObservations(goalText, draft, answers = {}, helpers = {}) {
    const observations = [];
    const nodes = Array.isArray(draft?.nodes) ? draft.nodes : [];
    const axes = Array.isArray(draft?.axes) ? draft.axes : [];
    const findDraftConditionNode = helpers.findDraftConditionNode || ((value) => (value?.nodes || []).find((node) => node.type === "condition") || null);
    const hasDraftConnection = helpers.hasDraftConnection || ((value, predicate) => (value?.connections || []).some(predicate));
    const findDraftMotionById = helpers.findDraftMotionById || (() => null);
    const extractAxisNameFromText = helpers.extractAxisNameFromText || (() => "");
    const detectOffBranchFromText = helpers.detectOffBranchFromText || (() => "");
    const detectAfterMotionFromText = helpers.detectAfterMotionFromText || (() => "");
    const extractTimeoutSecFromText = helpers.extractTimeoutSecFromText || (() => "");
    const extractErrorCodeFromText = helpers.extractErrorCodeFromText || (() => "");
    const hasCondition = nodes.some((node) => node.type === "condition");
    const condition = findDraftConditionNode(draft);

    if (!axes.length) {
        observations.push(createAgentObservation("AXIS_NAME_MISSING", "error", "軸がありません。", {
            path: "axes",
            questionId: "axis_name",
            suggestedAction: "ASK_AXIS_NAME"
        }));
    } else if (!extractAxisNameFromText(goalText) && !answers.axis_name) {
        observations.push(createAgentObservation("AXIS_NAME_MISSING", "question", "使用軸がまだ確定していません。", {
            path: "axes.0.name",
            questionId: "axis_name",
            suggestedAction: "ASK_AXIS_NAME"
        }));
    }
    if (!nodes.some((node) => node.type === "start")) {
        observations.push(createAgentObservation("START_NODE_MISSING", "error", "開始ノードがありません。", {
            path: "nodes",
            suggestedAction: "REVISE_DRAFT"
        }));
    }
    if (!nodes.some((node) => node.type === "end")) {
        observations.push(createAgentObservation("END_NODE_MISSING", "error", "終了ノードがありません。", {
            path: "nodes",
            suggestedAction: "REVISE_DRAFT"
        }));
    }
    if (hasCondition && !detectOffBranchFromText(goalText) && !answers.off_branch) {
        observations.push(createAgentObservation("OFF_BRANCH_MISSING", "question", "OFF 分岐先が未確定です。", {
            path: "nodes.cond1.outputs.output_2",
            questionId: "off_branch",
            suggestedAction: "ASK_OFF_BRANCH"
        }));
    }
    if (hasCondition && !extractTimeoutSecFromText(goalText) && !answers.timeout_sec) {
        observations.push(createAgentObservation("TIMEOUT_SEC_MISSING", "question", "タイムアウト秒数が未確定です。", {
            path: "nodes.cond1.timeoutSec",
            questionId: "timeout_sec",
            suggestedAction: "ASK_TIMEOUT_SEC"
        }));
    }
    if (hasCondition && !extractErrorCodeFromText(goalText) && !answers.error_code) {
        observations.push(createAgentObservation("ERROR_CODE_MISSING", "question", "タイムアウト時のエラーコードが未確定です。", {
            path: "nodes.err_timeout.errorCode",
            questionId: "error_code",
            suggestedAction: "ASK_ERROR_CODE"
        }));
    }
    if (axes.some((axis) => (axis.motions || []).length) && !detectAfterMotionFromText(goalText) && !answers.after_motion) {
        observations.push(createAgentObservation("AFTER_MOTION_MISSING", "question", "最後の動作後の処理が未確定です。", {
            path: "axes.0.motions",
            questionId: "after_motion",
            suggestedAction: "ASK_AFTER_MOTION"
        }));
    }
    for (const connection of draft?.connections || []) {
        const hasFrom = nodes.some((node) => node.id === connection.from);
        const hasTo = nodes.some((node) => node.id === connection.to);
        if (!hasFrom || !hasTo) {
            observations.push(createAgentObservation("CONNECTION_TARGET_MISSING", "error", `接続 ${connection.from} -> ${connection.to} の参照先がありません。`, {
                path: "connections",
                suggestedAction: "REVISE_DRAFT"
            }));
        }
    }
    for (const node of nodes) {
        if (node.type !== "motion") continue;
        if (!findDraftMotionById(draft, node.motionId)) {
            observations.push(createAgentObservation("MOTION_BINDING_MISSING", "error", `動作ノード ${node.id} に対応する motion がありません。`, {
                path: `nodes.${node.id}`,
                suggestedAction: "REVISE_DRAFT"
            }));
        }
    }
    if (
        condition &&
        Number.isFinite(Number(condition.timeoutSec)) &&
        !hasDraftConnection(draft, (connection) => connection.from === condition.id && (connection.output || "output_1") === "output_3")
    ) {
        observations.push(createAgentObservation("CONNECTION_TARGET_MISSING", "error", "TIMEOUT 分岐の接続先がありません。", {
            path: `nodes.${condition.id}.outputs.output_3`,
            suggestedAction: "REVISE_DRAFT"
        }));
    }
    return observations;
}

export function validateAgentDraft(draft, helpers = {}) {
    const findings = [];
    const nodes = Array.isArray(draft?.nodes) ? draft.nodes : [];
    const axes = Array.isArray(draft?.axes) ? draft.axes : [];
    const nodeIds = new Set(nodes.map((node) => node.id));
    const findDraftMotionById = helpers.findDraftMotionById || (() => null);

    if (!axes.length) {
        findings.push(createAgentObservation("AXIS_NAME_MISSING", "error", "軸がありません。", {
            path: "axes",
            suggestedAction: "REVISE_DRAFT"
        }));
    }
    if (!nodes.length) {
        findings.push(createAgentObservation("START_NODE_MISSING", "error", "ノードがありません。", {
            path: "nodes",
            suggestedAction: "REVISE_DRAFT"
        }));
    }
    for (const connection of draft?.connections || []) {
        if (!nodeIds.has(connection.from) || !nodeIds.has(connection.to)) {
            findings.push(createAgentObservation("CONNECTION_TARGET_MISSING", "error", `接続 ${connection.from} -> ${connection.to} の参照先がありません。`, {
                path: "connections",
                suggestedAction: "REVISE_DRAFT"
            }));
        }
    }
    for (const node of nodes) {
        if (node.type !== "motion") continue;
        if (!findDraftMotionById(draft, node.motionId)) {
            findings.push(createAgentObservation("MOTION_BINDING_MISSING", "error", `動作ノード ${node.id} に対応する motion がありません。`, {
                path: `nodes.${node.id}`,
                suggestedAction: "REVISE_DRAFT"
            }));
        }
    }
    return findings;
}
